import React, { Dispatch, memo, SetStateAction, useCallback, useMemo, useState } from 'react';

import { SliderCheckbox } from '@components/common';
import TabIconSection from '@components/common/TabIconSection/TabIconSection';
import Table from '@components/common/Table/Table';

import { useMutateExportHcAwtgRanPathParameter } from '@hooks/historyCheck/useMutateExportHcAwtgRanPathParameter';
import { useMutateExportHcParameters } from '@hooks/historyCheck/useMutateExportHcParameters';

import { formatDateByAggregationType } from '@utils/date';

import { ITablePagination } from '@interfaces/components/table';

import { defaultMeta, ETimeAggregationTypes } from '@enums/common';

import { timezoneUTC } from '@constants/common';
import { EHistoryParametersTabsName, historyParametersTabs } from './constants';

import HistoryChart from './HistoryCheckChart';
import HistoryCheckSectionHeader from './HistoryCheckSectionHeader';
import { getColumns } from './manageHistoryCheckParameter';

import styles from './HistoryCheckParameter.module.scss';

interface IHistoryCheckParameterProps {
	values: {
		value: string;
		date: number;
	}[];
	parametersToShow: Record<string, string>;
	sameDay?: number;
	parametersId?: string;
	vendor?: string;
	isOnlyChanged: boolean;
	setIsOnlyChanged: Dispatch<SetStateAction<boolean>>;
	isAwtgRanVendor?: boolean;
	dataModelPathId?: string;
	dumpHistoryId?: string;
}

const HistoryCheckParameter = ({
	values,
	parametersToShow,
	sameDay,
	vendor,
	parametersId,
	isOnlyChanged,
	setIsOnlyChanged,
	isAwtgRanVendor,
	dataModelPathId,
	dumpHistoryId,
}: IHistoryCheckParameterProps) => {
	const isChartViewDisabled = useMemo(() => values.some((item) => isNaN(item.value as any)), [values]);
	const defaultTab = EHistoryParametersTabsName.Table;
	const sameDayFormatted = sameDay ? formatDateByAggregationType({
		date: sameDay,
		organizationTimezone: timezoneUTC,
		timeAggregationType: ETimeAggregationTypes.dayMonthYear,
	}) : undefined;

	const [currentTab, setCurrentTab] = useState<string>(defaultTab);
	const [page, setPage] = useState<number>(defaultMeta.page);
	const [pageSize, setPageSize] = useState<number>(defaultMeta.pageSize);

	const { exportParameters } = useMutateExportHcParameters();
	const { exportParameters: exportAwtgRanParameters } = useMutateExportHcAwtgRanPathParameter();

	const formattedValues = useMemo(
		() => values.map((value) => {
			return {
				date: formatDateByAggregationType({
					date: value.date,
					organizationTimezone: timezoneUTC,
					timeAggregationType: sameDay
						? ETimeAggregationTypes.hourMinutes
						: ETimeAggregationTypes.dayMonthYear,
				}),
				value: value.value,
			};
		}), [values, sameDay]);

	const onToggleOnlyChanged = useCallback(() => {
		setIsOnlyChanged(prevState => !prevState);
	}, [setIsOnlyChanged]);

	const currentData = useMemo(() => {
		return formattedValues.slice((page - 1) * pageSize, page * pageSize);
	}, [formattedValues, page, pageSize]);

	const countOfRecords = formattedValues.length;

	const exportHandler = useCallback(() => {
		if (isAwtgRanVendor) {
			if (!parametersId || !dataModelPathId || !dumpHistoryId) return;

			return exportAwtgRanParameters({
				dataModelPathId,
				dumpHistoryId,
				id: parametersId,
			});
		}

		if (!parametersId && !vendor) return;

		return parametersId && exportParameters({ vendor, id: parametersId });
	}, [
		isAwtgRanVendor,
		dataModelPathId,
		dumpHistoryId,
		parametersId,
		exportAwtgRanParameters,
		exportParameters,
		vendor,
	]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPage(defaultMeta.page);
		setPageSize(pageSize);
	}, []);

	const columns = useMemo(() => getColumns(sameDayFormatted), [sameDayFormatted]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler: pageHandler,
		pageSizeHandler: pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const getCurrentTab = useCallback(() => {
		switch (currentTab) {
			case EHistoryParametersTabsName.Chart:
				return (
					<div className={styles.tabWrapper}>
						<HistoryCheckSectionHeader parametersToShow={parametersToShow} />
						<HistoryChart
							chartData={formattedValues}
							selectedDate={sameDayFormatted}
						/>
					</div>
				);
			case EHistoryParametersTabsName.Table:
				return (
					<div className={styles.tabWrapper}>
						<HistoryCheckSectionHeader parametersToShow={parametersToShow} />
						<Table
							columns={columns}
							customRightAction={
								<SliderCheckbox
									checked={isOnlyChanged}
									id='showChangedValueOnly'
									label='Show changed values only'
									onChange={onToggleOnlyChanged}
								/>
							}
							data={currentData}
							exportTooltipText='Export History to Excel'
							isLoading={false}
							isWithoutActions
							isWithoutCheckbox
							isWithoutDeleteAction
							isWithoutFilter
							pagination={pagination}
						/>
					</div>
				);
			default:
				return <></>;
		}
	}, [
		currentTab,
		formattedValues,
		parametersToShow,
		columns, currentData,
		pagination,
		sameDayFormatted,
		isOnlyChanged,
		onToggleOnlyChanged,
	]);

	return (
		<TabIconSection
			currentTab={currentTab}
			exportHandler={exportHandler}
			getCurrentTab={getCurrentTab}
			maxWidth='auto'
			setCurrentTab={setCurrentTab}
			tabs={isChartViewDisabled ? historyParametersTabs.slice(1) : historyParametersTabs}
		/>
	);
};

export default memo(HistoryCheckParameter);
